import { useCallback, useState } from 'react';
import type { ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, Text, Textarea } from '@mantine/core';

import { ModalContent } from '@shipstation/client-common/components/modal-content';
import { ModalFooter } from '@shipstation/client-common/components/modal-footer';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import { messages } from './messages';

const MAX_OVERRIDE_REASON_LENGTH = 400;

type PackingRecommendationOverrideReasonModalProps = {
  opened: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
};

export const PackingRecommendationOverrideReasonModal = ({ opened, onCancel, onSubmit }: PackingRecommendationOverrideReasonModalProps) => {
  const intl = useIntl();
  const zIndex = useMaxZ();

  return (
    <Modal
      onClose={onCancel}
      opened={opened}
      size='sm'
      title={
        <Text fw={600} fz='lg'>
          {intl.formatMessage(messages.overrideReasonModalHeader)}
        </Text>
      }
      zIndex={zIndex}
    >
      <PackingRecommendationOverrideReasonModalContent onCancel={onCancel} onSubmit={onSubmit} />
    </Modal>
  );
};

type PackingRecommendationOverrideReasonModalContentProps = {
  onCancel: () => void;
  onSubmit: (reason: string) => void;
};

const PackingRecommendationOverrideReasonModalContent = ({ onCancel, onSubmit }: PackingRecommendationOverrideReasonModalContentProps) => {
  const intl = useIntl();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleReasonChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setReason(event.currentTarget.value);
    setError(null);
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      setError(intl.formatMessage(messages.overrideReasonRequiredError));
      return;
    }

    onSubmit(trimmedReason);
  }, [reason, onSubmit, intl]);

  return (
    <>
      <ModalContent>
        <Text mb='sm' size='sm'>
          <FormattedMessage {...messages.overrideReasonModalDescription} />
        </Text>
        <Textarea
          error={error}
          label={intl.formatMessage(messages.overrideReasonLabel)}
          maxLength={MAX_OVERRIDE_REASON_LENGTH}
          onChange={handleReasonChange}
          value={reason}
        />
      </ModalContent>
      <ModalFooter>
        <Button onClick={onCancel} variant='tertiary'>
          <FormattedMessage {...messages.overrideReasonCancel} />
        </Button>
        <Button disabled={Boolean(error)} onClick={handleSubmit} variant='primary'>
          <FormattedMessage {...messages.overrideReasonSubmit} />
        </Button>
      </ModalFooter>
    </>
  );
};
